import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
    Stack,
    Avatar,
    Button,
    TableRow,
    MenuItem,
    TableCell,
    IconButton,
    Typography,
} from '@mui/material';
import Label from '../../components/label';
import Iconify from '../../components/iconify';
import MenuPopover from '../../components/menu-popover';
import ConfirmDialog from '../../components/confirm-dialog';
import RoleBasedGuard from 'src/auth/RoleBasedGuard';

// ----------------------------------------------------------------------

export default function DynamicTableRow({
    row,
    columns,
    onEditRow,
    onDeleteRow,
    onRowClick, // ✅ Tambahkan prop baru
    enableAvatar = false,
    nameField = 'name',
    controlEditDelete = true,
    controlEdit = true,
    controlDelete = true,
    deleteRoles = null, // jika diisi (array role id/name), menu Delete hanya tampil untuk role tsb
    deleteContent = 'Are you sure want to delete this item?' // isi/keterangan pada dialog konfirmasi hapus
}) {
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openPopover, setOpenPopover] = useState(null);

    const handleOpenConfirm = () => setOpenConfirm(true);
    const handleCloseConfirm = () => setOpenConfirm(false);
    const handleOpenPopover = (event) => setOpenPopover(event.currentTarget);
    const handleClosePopover = () => setOpenPopover(null);

    // ✅ Handle klik baris secara aman
    const handleRowClick = (event) => {
        // Jangan trigger kalau klik berasal dari tombol, switch, atau icon
        const tagName = event.target.tagName.toLowerCase();
        const stopTags = ['button', 'svg', 'path', 'input'];
        if (stopTags.includes(tagName)) return;

        onRowClick?.(row); // panggil callback jika diset
    };

    return (
        <>
            <TableRow hover sx={{ cursor: onRowClick ? 'pointer' : 'default' }} onClick={handleRowClick}>
                {columns.map((col) => {
                    const value = row[col.id];

                    // Avatar + name field
                    if (col.id === nameField && enableAvatar) {
                        return (
                            <TableCell key={col.id}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar alt={row[nameField]} src={row.avatarUrl} />
                                    <Typography variant="subtitle2" noWrap>
                                        {row[nameField]}
                                    </Typography>
                                </Stack>
                            </TableCell>
                        );
                    }

                    if (col.id === '') return null;

                    return (
                        <TableCell key={col.id} align={col.align || 'left'}>
                            {React.isValidElement(value) ? value : value ?? '-'}
                        </TableCell>
                    );
                })}

                {controlEditDelete && (
                    <RoleBasedGuard roles={[1, 2, 4]}>
                        <TableCell align="right">
                            <IconButton color={openPopover ? 'inherit' : 'default'} onClick={handleOpenPopover}>
                                <Iconify icon="eva:more-vertical-fill" />
                            </IconButton>
                        </TableCell>
                    </RoleBasedGuard>
                )}
            </TableRow>

            {/* Popover Actions */}
            <MenuPopover
                open={openPopover}
                onClose={handleClosePopover}
                arrow="right-top"
                sx={{ width: 140 }}
            >
                {controlEdit && (
                    <MenuItem
                        onClick={() => {
                            onEditRow?.();
                            handleClosePopover();
                        }}
                    >
                        <Iconify icon="eva:edit-fill" />
                        Edit
                    </MenuItem>
                )}

                {controlDelete && (
                    // deleteRoles kosong/null => RoleBasedGuard mengizinkan semua role (perilaku lama)
                    <RoleBasedGuard roles={deleteRoles || []}>
                        <MenuItem
                            onClick={() => {
                                handleOpenConfirm();
                                handleClosePopover();
                            }}
                            sx={{ color: 'error.main' }}
                        >
                            <Iconify icon="eva:trash-2-outline" />
                            Delete
                        </MenuItem>
                    </RoleBasedGuard>
                )}
            </MenuPopover>

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                open={openConfirm}
                onClose={handleCloseConfirm}
                title="Delete"
                content={deleteContent}
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            onDeleteRow?.();
                            handleCloseConfirm();
                        }}
                    >
                        Delete
                    </Button>
                }
            />
        </>
    );
}

// ----------------------------------------------------------------------

DynamicTableRow.propTypes = {
    row: PropTypes.object.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string,
            align: PropTypes.oneOf(['left', 'right', 'center']),
        })
    ).isRequired,
    onEditRow: PropTypes.func,
    onDeleteRow: PropTypes.func,
    onRowClick: PropTypes.func, // ✅ Tambah prop baru
    enableAvatar: PropTypes.bool,
    nameField: PropTypes.string,
    controlEditDelete: PropTypes.bool,
    controlEdit: PropTypes.bool,
    controlDelete: PropTypes.bool,
    deleteRoles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    deleteContent: PropTypes.node,
};
